import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { AxiosApiError } from '@/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { REPORT_QUERY_KEY } from './useGetReport';
import { LIST_REPORTS_QUERY_KEY } from './useFetchListReports';
import { GetReportResponse } from '@/api/generatedApi';
import { AxiosResponse } from 'axios';

export const useUpdateReportName = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();

  const tenantId = getTenantId();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.reports.updateReport(tenantId, id, { name }),
    onMutate: async ({ id, name }) => {
      const previousData = queryClient.getQueriesData({ queryKey: [REPORT_QUERY_KEY, { id, tenantId }] });

      queryClient.setQueriesData<AxiosResponse<GetReportResponse>>(
        { queryKey: [REPORT_QUERY_KEY, { id, tenantId }] },
        old => {
          if (!old?.data?.report) return old;
          return {
            ...old,
            data: {
              ...old.data,
              report: { ...old.data.report, name },
            },
          };
        }
      );

      return { previousData };
    },
    onError: (error: AxiosApiError, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      onError?.(error.message || 'Failed to update report name');
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [REPORT_QUERY_KEY, { id, tenantId }] });
      queryClient.resetQueries({ queryKey: [LIST_REPORTS_QUERY_KEY], exact: false });
      onSuccess?.();
    },
  });

  return {
    mutate,
    isPending,
  };
};
